/**
 * Sanitized Logging Utility
 * =========================
 * Prevents sensitive information exposure in production logs
 * while maintaining full error details in development.
 */

/**
 * Logs error messages safely, hiding sensitive details in production
 * @param {string} message - User-friendly error message
 * @param {Error|any} error - The error object (full details only shown in dev)
 */
export const logError = (message, error = null) => {
  if (process.env.NODE_ENV === 'production') {
    // Production: Only log the sanitized message
    console.error(message);
  } else {
    // Development: Log full error details for debugging
    if (error) {
      console.error(message, error);
    } else {
      console.error(message);
    }
  }
};

/**
 * Logs warning messages safely
 * @param {string} message - Warning message
 * @param {any} details - Additional details (only shown in dev)
 */
export const logWarning = (message, details = null) => {
  if (process.env.NODE_ENV === 'production') {
    console.warn(message);
  } else {
    if (details) {
      console.warn(message, details);
    } else {
      console.warn(message);
    }
  }
};

/**
 * Logs info messages (always shown but without sensitive details in prod)
 * @param {string} message - Info message
 * @param {any} details - Additional details (only shown in dev)
 */
export const logInfo = (message, details = null) => {
  if (process.env.NODE_ENV === 'production') {
    console.log(message);
  } else {
    if (details) {
      console.log(message, details);
    } else {
      console.log(message);
    }
  }
};

/**
 * Safely extracts error message from various error types
 * @param {Error|string|any} error - The error to extract message from
 * @returns {string} Safe error message
 */
export const getSafeErrorMessage = (error) => {
  if (typeof error === 'string') {
    return error;
  }
  
  if (error && typeof error === 'object') {
    return error.message || error.toString() || 'An unknown error occurred';
  }
  
  return 'An unknown error occurred';
};

export default {
  logError,
  logWarning,
  logInfo,
  getSafeErrorMessage
};